import {
  connect,
  eventBus,
  INTERNAL_EVENTS,
  updateConfig,
  useBaseStore,
  useConnectionStore,
  useParticipantStore,
  type AllEvents,
  type AudioInputDevice,
  type AudioOutputDevice,
  type ConfigStateBoth,
  type ConfigStateWeb,
  type VideoInputDevice,
  type WebSDKEvents,
} from '@cometchat/calls-sdk-core';
import * as v from 'valibot';
import { resetAllStores } from '../../../calls-sdk-core/store/utils/reset';
import { joinSessionInternal } from '../web/sdk-js';
import { CallAppSettingsSchema, type CallAppSettings } from './app-settings';
import { CALL_CONSTANTS } from './constants';
import { executeHTTPRequest } from './http-request';
import { SessionMethods } from './SessionMethods';
import type { Result } from './types';
import { APIHandler } from './v4/api';
import { OngoingCallListener } from './v4/Listener';
import {
  CallAppSettingsBuilder,
  CallSettingsBuilder,
  CallAppSettings as LegacyCallAppSettings,
  CallSettings as LegacyCallSettings,
} from './v4/models';
import { CallLog } from './v4/models/CallLog';
import { CallLogRequestBuilder } from './v4/models/CometChatCallLogs';
import { callVerifyTokenAPI, type VerifyTokenException } from './verify-token';
// Types and interfaces
interface User {
  uid: string;
  name: string;
  status?: string;
  authToken?: string;
  lastActiveAt?: number;
  avatar?: string; // URL to user avatar image
  role?: string;
  hasBlockedMe?: boolean;
  blockedByMe?: boolean;
  deactivatedAt?: number;
  wsChannel?: {
    identity: string;
  };
  jwt?: string;
  // Add other user properties as needed
}

interface CometChatException {
  errorCode: string;
  errorDescription: string;
  message?: string;
}

interface LoginListener {
  onLoginSuccess?: (user: User) => void;
  onLoginFailure?: (error: CometChatException) => void;
  onLogoutSuccess?: () => void;
  onLogoutFailure?: (error: CometChatException) => void;
}

export type SessionSettings = Partial<ConfigStateWeb & ConfigStateBoth>;

export class CometChatCalls extends SessionMethods {
  private static loggedInUser: User | null = null;
  private static isInitialized: boolean = false;
  private static loginInProgress: boolean = false;
  private static appSettings: CallAppSettings | null = null;
  private static loginListeners: Map<string, LoginListener> = new Map();
  public static constants = CALL_CONSTANTS;
  public static CallLogRequestBuilder = CallLogRequestBuilder;
  public static CallLog = CallLog;

  // v4 related changes start here

  /**
   * @deprecated Use CometChatCalls.init passing object directly.
   */
  public static CallAppSettingsBuilder = CallAppSettingsBuilder;

  /**
   * @deprecated Use CometChatCalls.joinSession passing object directly.
   */
  public static CallSettingsBuilder = CallSettingsBuilder;

  /**
   * @deprecated Use CometChatCalls.addEventListener
   */
  public static OngoingCallListener = OngoingCallListener;
  // v4 related changes end here

  // Initialize the SDK
  public static async init(
    appSettings: CallAppSettings | LegacyCallAppSettings,
  ) {
    const parsedAppSettings = v.safeParse(CallAppSettingsSchema, appSettings);
    if (!parsedAppSettings.success) {
      console.error(
        'App settings validation failed:',
        parsedAppSettings.issues,
      );
      return {
        success: false,
        error: {
          name: 'VALIDATION_ERROR',
          message: `Invalid app settings: ${parsedAppSettings.issues.map(issue => issue.message).join(', ')}`,
          timestamp: Date.now(),
        },
      } as const;
    }

    this.appSettings = parsedAppSettings.output;
    this.isInitialized = true;

    // For v4 compatibility
    APIHandler.setAppSettings({
      ...this.appSettings,
      host: `${this.getBaseURL('call')}/`,
    });

    eventBus.publish({
      type: INTERNAL_EVENTS.lifecycle.SDKInitialized,
    });

    // Check if user was previously logged in (from localStorage/sessionStorage)
    const savedUser = this.getSavedUser();
    if (savedUser) {
      let parsedUser;
      if (typeof savedUser === 'string') {
        try {
          parsedUser = JSON.parse(savedUser);
        } catch (_e) {
          parsedUser = savedUser;
        }
      } else {
        parsedUser = savedUser;
      }
      this.loggedInUser = parsedUser;
    }
    return {
      success: true,
      error: null,
    } as const;
  }

  // Login with UID and Auth Key
  public static async login(uid: string, authKey?: string): Promise<User> {
    try {
      // Validation checks
      if (this.loginInProgress) {
        throw {
          errorCode: 'ERROR_LOGIN_IN_PROGRESS',
          errorDescription: 'Login is already in progress',
        };
      }

      if (!uid || uid.trim() === '') {
        throw {
          errorCode: 'ERROR_INVALID_UID',
          errorDescription: 'UID cannot be empty',
        };
      }

      if (uid.includes(' ')) {
        throw {
          errorCode: 'ERROR_UID_WITH_SPACE',
          errorDescription: 'UID cannot contain spaces',
        };
      }

      const resolvedAuthKey = authKey || this.appSettings?.authKey;

      if (!resolvedAuthKey || resolvedAuthKey.trim() === '') {
        throw {
          errorCode: 'ERROR_API_KEY_NOT_FOUND',
          errorDescription: 'Auth Key cannot be empty',
        };
      }

      if (!this.isInitialized) {
        throw {
          errorCode: 'ERROR_SDK_NOT_INITIALIZED',
          errorDescription: 'SDK not initialized. Call init() first',
        };
      }

      this.loginInProgress = true;

      // Check if user is already logged in with same UID
      if (
        this.loggedInUser &&
        this.loggedInUser.uid === uid &&
        this.loggedInUser.authToken
      ) {
        this.loginInProgress = false;
        this.notifyLoginSuccess(this.loggedInUser);
        return this.loggedInUser;
      }

      // If different user is logged in, logout first
      if (this.loggedInUser && this.loggedInUser.uid !== uid) {
        await this.logoutInternal();
      }

      // Perform login API call to get auth token
      const authToken = await this.loginWithUID(uid, resolvedAuthKey);

      // Authenticate with the received token to get user details
      const user = await this.authenticateWithToken(authToken);

      this.loginInProgress = false;
      this.loggedInUser = user;
      this.saveUser(user);
      this.notifyLoginSuccess(user);
      return user;
    } catch (error) {
      this.loginInProgress = false;
      const cometChatError = this.createError(error);
      this.notifyLoginFailure(cometChatError);
      throw cometChatError;
    }
  }

  // Login with auth token directly
  public static async loginWithAuthToken(authToken: string): Promise<User> {
    try {
      if (this.loginInProgress) {
        throw {
          errorCode: 'ERROR_LOGIN_IN_PROGRESS',
          errorDescription: 'Login is already in progress',
        };
      }

      if (!authToken || authToken.trim() === '') {
        throw {
          errorCode: 'ERROR_BLANK_AUTHTOKEN',
          errorDescription: 'Auth token cannot be empty',
        };
      }

      if (authToken.includes(' ')) {
        throw {
          errorCode: 'ERROR_AUTHTOKEN_WITH_SPACE',
          errorDescription: 'Auth token cannot contain spaces',
        };
      }

      this.loginInProgress = true;

      // Check if same token is already used
      if (this.loggedInUser && this.loggedInUser.authToken === authToken) {
        this.loginInProgress = false;
        this.notifyLoginSuccess(this.loggedInUser);
        return this.loggedInUser;
      }

      // If different user is logged in, logout first
      if (this.loggedInUser && this.loggedInUser.authToken !== authToken) {
        await this.logoutInternal();
      }

      const user = await this.authenticateWithToken(authToken);

      this.loginInProgress = false;
      this.loggedInUser = user;
      this.saveUser(user);
      this.notifyLoginSuccess(user);
      return user;
    } catch (error) {
      this.loginInProgress = false;
      const cometChatError = this.createError(error);
      this.notifyLoginFailure(cometChatError);
      throw cometChatError;
    }
  }

  // Logout functionality
  public static async logout(): Promise<string> {
    try {
      if (!this.loggedInUser) {
        throw {
          errorCode: 'ERROR_NO_USER_LOGGED_IN',
          errorDescription: 'No user is currently logged in',
        };
      }

      await this.logoutInternal();
      this.notifyLogoutSuccess();
      return 'Logout successful';
    } catch (error) {
      const cometChatError = this.createError(error);
      this.notifyLogoutFailure(cometChatError);
      throw cometChatError;
    }
  }

  // Get logged in user
  public static getLoggedInUser(): User | null {
    // Verify it's always an object
    if (this.loggedInUser && typeof this.loggedInUser === 'string') {
      this.loggedInUser = JSON.parse(this.loggedInUser);
    }
    return this.loggedInUser;
  }

  // Get user auth token
  public static getUserAuthToken(): string | null {
    return this.loggedInUser?.authToken || null;
  }

  // Check if user is logged in
  public static isUserLoggedIn(): boolean {
    return (
      this.loggedInUser !== null && this.loggedInUser.authToken !== undefined
    );
  }

  // Add login listener
  public static addLoginListener(
    listenerId: string,
    listener: LoginListener,
  ): void {
    this.loginListeners.set(listenerId, listener);
  }

  // Remove login listener
  public static removeLoginListener(listenerId: string): void {
    this.loginListeners.delete(listenerId);
  }

  // Generate token (enhanced to use logged-in user's authToken)
  public static async generateToken(sessionId: string, authToken?: string) {
    try {
      if (!sessionId || sessionId.trim() === '') {
        throw {
          errorCode: 'ERROR_SESSION_ID_MISSING',
          errorDescription: 'Session ID cannot be empty',
        };
      }

      // Use provided authToken or get from logged-in user
      const tokenToUse = authToken || this.getUserAuthToken();

      if (!tokenToUse) {
        throw {
          errorCode: 'ERROR_AUTH_TOKEN_MISSING',
          errorDescription:
            'Auth token is required. Please login first or provide authToken.',
        };
      }

      if (!this.isInitialized) {
        throw {
          errorCode: 'ERROR_SDK_NOT_INITIALIZED',
          errorDescription: 'SDK not initialized',
        };
      }

      // Make API call to generate token
      const token = await this.callGenerateTokenAPI(sessionId, tokenToUse);
      return { token };
    } catch (error) {
      throw this.createError(error);
    }
  }

  private static getBaseURL(
    subdomain: 'call' | 'api' | 'apiclient' = 'call',
  ): string {
    if (subdomain === 'call' && this.appSettings?.host) {
      return this.appSettings.host;
    }
    if (subdomain === 'api' && this.appSettings?.adminHost) {
      return this.appSettings.adminHost;
    }
    if (subdomain === 'apiclient' && this.appSettings?.clientHost) {
      return this.appSettings.clientHost;
    }
    const appId = this.appSettings?.appId || '';
    const region = this.appSettings?.region || '';
    const domain = 'cometchat.io';
    return `https://${appId}.${subdomain}-${region}.${domain}/v3.0`;
  }

  // Private helper methods
  private static async loginWithUID(
    uid: string,
    authKey: string,
  ): Promise<string> {
    const appId = this.appSettings?.appId || '';

    const response = await executeHTTPRequest<{ authToken: string }>({
      baseURL: this.getBaseURL('api'),
      method: 'POST',
      endpoint: `users/${uid}/auth_tokens`,
      headers: {
        'Content-Type': 'application/json',
        apiKey: authKey,
        appId,
      },
      body: {
        platform: 'web',
        deviceId: this.generateDeviceId(),
      },
    });

    if (response.error) {
      throw new Error(`Login failed: ${response.error.message}`);
    }

    return response.data.authToken;
  }

  private static async authenticateWithToken(authToken: string): Promise<User> {
    const appId = this.appSettings?.appId || '';

    const response = await executeHTTPRequest<{
      uid: string;
      name: string;
      status?: string;
      avatar?: string;
      lastActiveAt?: number;
      role?: string;
      jwt?: string;
    }>({
      baseURL: this.getBaseURL('apiclient'),
      method: 'PUT',
      endpoint: 'me',
      headers: {
        'Content-Type': 'application/json',
        authToken,
        appId,
      },
      body: {
        platform: 'web',
        deviceId: this.generateDeviceId(),
      },
    });

    if (response.error) {
      throw new Error(`Authentication failed: ${response.error.message}`);
    }

    const data = response.data;
    return {
      uid: data.uid,
      name: data.name,
      authToken: authToken,
      status: data.status || 'online',
      avatar: data.avatar || '',
      lastActiveAt: data.lastActiveAt || Date.now(),
      role: data.role || 'default',
      hasBlockedMe: false,
      blockedByMe: false,
      deactivatedAt: 0,
      wsChannel: {
        identity: `[${appId}]${data.uid}`,
      },
      jwt: data.jwt,
    };
  }

  private static async logoutInternal(): Promise<void> {
    if (this.loggedInUser?.authToken) {
      // Make logout API call
      try {
        await executeHTTPRequest({
          baseURL: this.getBaseURL('apiclient'),
          method: 'DELETE',
          endpoint: 'me',
          headers: {
            authToken: this.loggedInUser.authToken,
            appId: this.appSettings?.appId || '',
          },
        });
      } catch (error) {
        console.warn('Logout API call failed:', error);
      }
    }

    // Clear local data
    this.loggedInUser = null;
    this.clearSavedUser();
  }

  private static async callGenerateTokenAPI(
    sessionId: string,
    authToken: string,
  ): Promise<string> {
    const appId = this.appSettings?.appId || '';

    const response = await executeHTTPRequest<{ token: string }>({
      baseURL: this.getBaseURL(),
      method: 'POST',
      endpoint: 'call_tokens',
      headers: {
        'Content-Type': 'application/json',
        authToken,
        appId,
      },
      body: {
        sessionId,
      },
    });

    if (response.error) {
      throw new Error(`Token generation failed: ${response.error.message}`);
    }

    return response.data.token;
  }

  private static async callVerifyTokenAPI(calltoken: string) {
    return callVerifyTokenAPI({
      appId: this.appSettings?.appId || '',
      region: this.appSettings?.region || '',
      calltoken: calltoken,
      baseURL: this.getBaseURL(),
    });
  }

  private static saveUser(user: User): void {
    try {
      const key = `${this.appSettings?.appId}:common_store/user`;
      const userWithDefaults = {
        hasBlockedMe: false,
        blockedByMe: false,
        deactivatedAt: 0,
        ...user,
        role: user.role || 'default',
        wsChannel: user.wsChannel || {
          identity: `[${this.appSettings?.appId}]${user.uid}`,
        },
      };
      localStorage.setItem(key, JSON.stringify(userWithDefaults));
    } catch (error) {
      console.warn('Failed to save user to localStorage:', error);
    }
  }

  private static getSavedUser(): string | null {
    try {
      const key = `${this.appSettings?.appId}:common_store/user`;
      const savedUser = localStorage.getItem(key);
      return savedUser ? savedUser : null;
    } catch (error) {
      console.warn('Failed to get saved user from localStorage:', error);
      return null;
    }
  }

  private static clearSavedUser(): void {
    try {
      const key = `${this.appSettings?.appId}:common_store/user`;
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear saved user from localStorage:', error);
    }
  }

  private static generateDeviceId(): string {
    return 'web_' + Math.random().toString(36).substr(2, 9);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static createError(error: any): CometChatException {
    if (error.errorCode && error.errorDescription) {
      return error;
    }
    return {
      errorCode: 'ERROR_UNKNOWN',
      errorDescription: error.message || 'An unknown error occurred',
    };
  }

  // Notification methods for listeners
  private static notifyLoginSuccess(user: User): void {
    this.loginListeners.forEach(listener => {
      if (listener.onLoginSuccess) {
        try {
          listener.onLoginSuccess(user);
        } catch (error) {
          console.error('Error in login success listener:', error);
        }
      }
    });
  }

  private static notifyLoginFailure(error: CometChatException): void {
    this.loginListeners.forEach(listener => {
      if (listener.onLoginFailure) {
        try {
          listener.onLoginFailure(error);
        } catch (err) {
          console.error('Error in login failure listener:', err);
        }
      }
    });
  }

  private static notifyLogoutSuccess(): void {
    this.loginListeners.forEach(listener => {
      if (listener.onLogoutSuccess) {
        try {
          listener.onLogoutSuccess();
        } catch (error) {
          console.error('Error in logout success listener:', error);
        }
      }
    });
  }

  private static notifyLogoutFailure(error: CometChatException): void {
    this.loginListeners.forEach(listener => {
      if (listener.onLogoutFailure) {
        try {
          listener.onLogoutFailure(error);
        } catch (err) {
          console.error('Error in logout failure listener:', err);
        }
      }
    });
  }

  public static addEventListener<K extends keyof WebSDKEvents>(
    eventType: K,
    listener: WebSDKEvents[K],
    options?: { signal?: AbortSignal },
  ) {
    return eventBus.subscribe(eventType, listener as AllEvents[K], options);
  }

  /**
   * @deprecated Use CometChatCalls.joinSession instead.
   */
  public static startSession(
    callToken: string,
    callSettings: LegacyCallSettings,
    container: HTMLElement,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _callSettings: any = callSettings;

    const newCallSettings: SessionSettings = {
      hideControlPanel: !(_callSettings.defaultLayout ?? false),
      hideLeaveSessionButton: !(_callSettings.ShowEndCallButton ?? true),
      hideRecordingButton: !(_callSettings.ShowRecordingButton ?? true),
      hideChangeLayoutButton: !(_callSettings.ShowSwitchModeButton ?? true),
      hideScreenSharingButton: !(_callSettings.ShowScreenShareButton ?? true),
      hideToggleAudioButton: !(_callSettings.ShowMuteAudioButton ?? true),
      hideToggleVideoButton: !(_callSettings.ShowPauseVideoButton ?? true),
      autoStartRecording: _callSettings.StartRecordingOnCallStart ?? false,
      hideNetworkIndicator: !(
        _callSettings.MainVideoContainerSetting?.network?.visibility ?? true
      ),
      hideVirtualBackgroundButton: !(
        _callSettings.ShowVirtualBackgroundSetting ?? true
      ),
      layout: _callSettings.mode === 'DEFAULT' ? 'SIDEBAR' : _callSettings.mode,
      startAudioMuted: _callSettings.StartAudioMuted ?? false,
      startVideoPaused: _callSettings.StartVideoMuted ?? false,
      idleTimeoutPeriodBeforePrompt: 60 * 1000,
      idleTimeoutPeriodAfterPrompt: Math.max(
        _callSettings.idleTimeoutPeriod ?? 120 * 1000,
        60 * 1000,
      ),
      sessionType:
        _callSettings.isAudioCall || _callSettings.isAudioOnly
          ? 'VOICE'
          : 'VIDEO',
    };
    const allListeners: Array<() => void> = [];
    allListeners.push(
      CometChatCalls.addEventListener('onLeaveSessionButtonClicked', () => {
        callSettings.getCallListener()?.onCallEndButtonPressed?.();
        allListeners.forEach(unsub => unsub());
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onSessionLeft', () => {
        callSettings.getCallListener()?.onCallEnded?.();
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onParticipantLeft', participant => {
        callSettings.getCallListener()?.onUserLeft?.(participant);
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onParticipantListChanged', userList => {
        const localParticipant =
          useParticipantStore.getState().localParticipant;
        callSettings.getCallListener()?.onUserListUpdated?.([
          {
            name: localParticipant.name,
            uid: localParticipant.uid,
            avatar: localParticipant.avatar,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
          ...userList,
        ]);
        if (userList.length === 0) {
          callSettings.getCallListener()?.onCallEnded?.();
        }
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onParticipantJoined', participant => {
        callSettings.getCallListener()?.onUserJoined?.(participant);
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onAudioMuted', () => {
        callSettings.getCallListener()?.onUserMuted?.({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          muted: { isAudioMuted: true } as any,
          mutedBy: undefined,
        });
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onParticipantAudioMuted', event => {
        callSettings.getCallListener()?.onUserMuted?.({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          muted: { ...event, isAudioMuted: true } as any,
          mutedBy: undefined,
        });
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onParticipantVideoPaused', event => {
        callSettings.getCallListener()?.onUserMuted?.({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          muted: { ...event, isVideoMuted: true } as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mutedBy: undefined as any,
        });
      }),
    );
    allListeners.push(
      CometChatCalls.addEventListener('onRecordingStarted', () => {
        // TODO: fix this
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        callSettings.getCallListener()?.onRecordingStarted?.(undefined as any);
      }),
    );

    allListeners.push(
      CometChatCalls.addEventListener('onRecordingStopped', () => {
        callSettings.getCallListener()?.onRecordingStopped?.();
      }),
    );

    return this.joinSession(callToken, newCallSettings, container);
  }

  private static async _connectToRoom(callToken: string) {
    resetAllStores();
    const { data, error } = await this.callVerifyTokenAPI(callToken);
    if (error) {
      return {
        data,
        error,
      };
    }
    updateConfig({
      internalSettings: JSON.stringify({ data }),
    });
  }

  private static async connectToRoom(callToken: string) {
    await this._connectToRoom(callToken);
    return connect(false);
  }

  public static async joinSession(
    callToken: string,
    callSettings: SessionSettings,
    container: HTMLElement,
  ): Promise<Result<void, VerifyTokenException>> {
    const connection = useConnectionStore.getState().connection;
    if (!connection) {
      await this._connectToRoom(callToken);
      // This is just to prevent undeclared var warning
      if (1 > 2) {
        this.connectToRoom(callToken);
      }
    }
    console.log('Joining session with settings', callSettings);
    updateConfig(callSettings);
    joinSessionInternal(container);
    return {
      data: undefined,
      error: null,
    };
  }

  public static getAudioInputDevices(): AudioInputDevice[] {
    return useBaseStore.getState().audioInputDevices;
  }

  public static getVideoInputDevices(): VideoInputDevice[] {
    return useBaseStore.getState().videoInputDevices;
  }

  public static getAudioOutputDevices(): AudioOutputDevice[] {
    return useBaseStore.getState().audioOutputDevices;
  }

  public static getCurrentAudioInputDevice(): AudioInputDevice | undefined {
    return useBaseStore.getState().audioInputDevice;
  }

  public static getCurrentVideoInputDevice(): VideoInputDevice | undefined {
    return useBaseStore.getState().videoInputDevice;
  }

  public static getCurrentAudioOutputDevice(): AudioOutputDevice | undefined {
    return useBaseStore.getState().audioOutputDevice;
  }
}

if (process.env.NODE_ENV === 'playwright-test') {
  window.CometChatCalls = CometChatCalls;
}

window.CometChatCalls = CometChatCalls;
